import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Zap, Trophy, RotateCcw } from 'lucide-react';

// States: idle | waiting | ready | tooSoon | done
export default function ReactionTime() {
  const [state, setState] = useState('idle');
  const [ms, setMs] = useState(null);
  const [attempts, setAttempts] = useState([]); // recent 5
  const startRef = useRef(0);
  const timeoutRef = useRef(null);
  const [best, setBest] = useState(() => {
    try { return Number(localStorage.getItem('spc_reaction_best')) || null; }
    catch { return null; }
  });

  const clearT = () => { if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; } };
  useEffect(() => () => clearT(), []);

  const beginRound = useCallback(() => {
    clearT();
    setMs(null);
    setState('waiting');
    const delay = 1200 + Math.random() * 2600; // 1.2s – 3.8s
    timeoutRef.current = setTimeout(() => {
      startRef.current = performance.now();
      setState('ready');
    }, delay);
  }, []);

  const onTap = () => {
    if (state === 'idle' || state === 'done' || state === 'tooSoon') {
      beginRound();
      return;
    }
    if (state === 'waiting') {
      clearT();
      setState('tooSoon');
      return;
    }
    if (state === 'ready') {
      const t = Math.round(performance.now() - startRef.current);
      setMs(t);
      setAttempts(a => [t, ...a].slice(0, 5));
      if (!best || t < best) {
        setBest(t);
        try { localStorage.setItem('spc_reaction_best', String(t)); } catch {}
      }
      setState('done');
    }
  };

  const label = {
    idle: 'Tap to start',
    waiting: 'Wait for GREEN…',
    ready: 'TAP!',
    tooSoon: 'Too soon — tap to retry',
    done: `${ms} ms`,
  }[state];

  const avg = attempts.length ? Math.round(attempts.reduce((a,b) => a+b, 0) / attempts.length) : null;

  return (
    <div className="solo-game" data-testid="solo-reaction">
      <div className="solo-head">
        <div className="solo-title"><Zap size={18} /><span>Reaction Time</span></div>
        <div className="solo-score">
          {best && <span className="pill yellow"><Trophy size={12} /> Best {best} ms</span>}
          {avg && <span className="pill">Avg {avg} ms</span>}
        </div>
      </div>

      <button
        className={`reaction-pad ${state}`}
        onClick={onTap}
        data-testid="reaction-pad"
        aria-label="Reaction pad"
      >
        <span className="reaction-label">{label}</span>
        {state === 'done' && <span className="reaction-sub">Tap for another go</span>}
        {state === 'idle' && <span className="reaction-sub">Wait for green, then tap as fast as you can</span>}
        {state === 'tooSoon' && <span className="reaction-sub">You jumped the gun</span>}
      </button>

      {attempts.length > 0 && (
        <div className="reaction-list" data-testid="reaction-history">
          <strong style={{ fontSize: 13 }}>Recent</strong>
          <div className="reaction-chips">
            {attempts.map((t, i) => <span key={i} className={`pill ${t === best ? 'yellow' : ''}`}>{t} ms</span>)}
          </div>
        </div>
      )}

      {attempts.length > 0 && (
        <div className="solo-actions">
          <button className="btn ghost" data-testid="reaction-clear" onClick={() => { setAttempts([]); setState('idle'); setMs(null); }}><RotateCcw size={14} /> Clear session</button>
        </div>
      )}
    </div>
  );
}
